import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import {
  BREAKPOINTS,
  FILTER_IDS,
  SearchApiResultsConstants as SearchResultsConstants,
  ZERO_INDEX_EQUALIZER,
} from '@src/js/constants.js';
import LocationResults from '@src/components/locations-results/location-results.jsx';
import LocationResultCard from '@src/components/locations-result-card/location-result-card.jsx';
import dayjs from 'dayjs';
import DirectionsService from '@src/js/services/directions-service.js';
import SearchApiService from '@src/js/services/search-api-service.js';
import ElementService from '@src/js/services/element-service.js';
import FindLocationsMap from '@js/find-locations-map.es6.js';
import GeolocationService from '@src/js/services/geolocation-service.js';
import { Pagination } from '@src/components/core/core-component-imports.js';
import FindALocationResultsContentType from '@js/content-types/find-a-location-results-content-type.es6.js';

export default class LocationsResultsContainer {
  constructor(searchString) {
    this.locationResultsContainer = document.querySelector('.locations-results--container');
    if (this.locationResultsContainer) {
      this.searchApiService = new SearchApiService();
      this.elementService = new ElementService();
      this.geolocationService = new GeolocationService();
      this.searchString = searchString;
      this.userLocation = JSON.parse(sessionStorage.getItem('userCurrentLatLng'));
      this.url = new URL(window.location);
      this.results = null;
      this.zeroIndexEqualizer = 1;
      this.daysOfWeekArray = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

      const findALocationResults = new FindALocationResultsContentType();
      this.countLabel = findALocationResults.countLabel.item.$(0).text();
      this.todaysHoursLabel = findALocationResults.todaysHoursLabel.item.$(0).text();
      this.directionsLabel = findALocationResults.directionsLabel.item.$(0).text();
      this.viewDetailsLabel = findALocationResults.viewLocationDetailsLabel.item.$(0).text();
      this.ofLabel = findALocationResults.ofLabel.item.$(0).text();
      this.pageLabel = findALocationResults.pageLabel.item.$(0).text();
      this.milesLabel = findALocationResults.mileslabel.item.$(0).text();
      this.awayLabel = findALocationResults.awayLabel.item.$(0).text();

      this.locationsResultsWrapper = document.querySelector('.location-results-grid--wrapper');
      this.paginationContainer = this.locationsResultsWrapper.querySelector('.pagination--container');
      this.loadingBox = this.locationsResultsWrapper.querySelector('.loading-box');
      this.siteHeader = document.querySelector('.site-header__inner__container');
      this.hideMapButton = this.locationsResultsWrapper.querySelector('.hide-map-button');
      this.showMapButton = this.locationsResultsWrapper.querySelector('.show-map-button');

      this.openDirections = this.openDirections.bind(this);
      this.getResults = this.getResults.bind(this);

      this.setListeners();
      this.setLeftMargin();

      const pageNumOneBased = this.url.searchParams.get(FILTER_IDS.PAGE);
      const pageNumZeroBased = pageNumOneBased - ZERO_INDEX_EQUALIZER;
      this.getResults(pageNumZeroBased);
    }
  }

  onShowMap() {
    this.locationsResultsWrapper.classList.remove('no-map');
    this.setLeftMargin();
  }

  onHideMap() {
    this.locationsResultsWrapper.classList.add('no-map');
    this.locationsResultsWrapper.style.removeProperty('margin-left');
  }

  setListeners() {
    window.addEventListener('resize', () => {
      this.setLeftMargin();
    });
    this.hideMapButton.addEventListener('click', () => {
      this.onHideMap();
    });
    this.showMapButton.addEventListener('click', () => {
      this.onShowMap();
    });
  }

  setLeftMargin() {
    if (window.innerWidth >= BREAKPOINTS.DESKTOP && !this.locationsResultsWrapper.classList.contains('no-map')) {
      const amountHeaderIsFromLeftOfWindow = this.siteHeader.getBoundingClientRect().left;
      this.locationsResultsWrapper.style.marginLeft = `${amountHeaderIsFromLeftOfWindow}px`;
    } else {
      this.locationsResultsWrapper.style.removeProperty('margin-left');
    }
  }

  showLoadedState() {
    this.elementService.toggleVisibleStateOfElement(this.loadingBox, false);
    this.elementService.toggleVisibleStateOfElement(this.locationResultsContainer, true);
    this.elementService.toggleVisibleStateOfElement(this.paginationContainer, true);
  }

  showLoadingState() {
    this.elementService.toggleVisibleStateOfElement(this.loadingBox, true);
    this.elementService.toggleVisibleStateOfElement(this.locationResultsContainer, false);
    this.elementService.toggleVisibleStateOfElement(this.paginationContainer, false);
  }

  async getResults(pageNum) {
    this.showLoadingState();
    if (this.url.searchParams.get(FILTER_IDS.LOCATION)) {
      this.userLocation = this.url.searchParams.get(FILTER_IDS.LOCATION);
    }
    if (this.userLocation) {
      this.results = await this.searchApiService.searchLuminisLocationsWithDistance(this.searchString, this.userLocation, pageNum);
    } else {
      this.results = await this.searchApiService.searchLuminisLocations(this.searchString, pageNum);
    }
    this.setPageInfo();
    this.updateContainer();
    this.updatePagination();
    new FindLocationsMap(this.results.rows);
  }

  openDirections(address) {
    const directionsService = new DirectionsService();
    directionsService.openDirections(address);
  }

  buildLocationCountText() {
    return `${this.results.pager[SearchResultsConstants.TOTAL_ITEMS]} ${this.countLabel}`;
  }

  getLocationImage(locationImageFromApi, altText) {
    if (locationImageFromApi) {
      return `<img src="${locationImageFromApi}" alt="${altText}" />`;
    } else {
      return `<svg class="location-default-image" width="80px" height="80px" viewBox="0 0 80 80" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
            <title>Location Pin</title>
            <defs><rect id="path-1" x="0" y="0" width="80" height="80"></rect></defs>
            <g id="Style-Guide/Pattern-Library" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
              <g id="Style-Guide---Logo-&amp;-Iconography" transform="translate(-112.000000, -2867.000000)">
                <g id="iconograhpy_location" transform="translate(112.000000, 2867.000000)">
                  <mask id="mask-2" fill="white"><use xlink:href="#path-1"></use></mask>
                  <g id="Mask"></g>
                  <g mask="url(#mask-2)" id="Group-13">
                    <g transform="translate(24.000000, 16.000000)">
                      <path d="M19.989 6.081 C11.245 6.081 4.131 13.196 4.131 21.941 C4.131 29.935 13.027 43.569 17.698 50.174 C18.833 51.779 21.204 51.798 22.363 50.211 C27.05 43.797 35.849 30.601 35.849 21.941 C35.849 13.196 28.735 6.081 19.989 6.081" id="Fill-52" fill="#005B55"></path>
                      <path d="M15.859 0 C7.115 0 0 7.115 0 15.86 C0 23.854 8.897 37.487 13.568 44.093 C14.702 45.698 17.073 45.717 18.232 44.13 C22.919 37.716 31.718 24.52 31.718 15.86 C31.718 7.115 24.604 0 15.859 0" id="Fill-53" fill="#FFFFFE"></path>
                      <path d="M15.86 21.985 C12.563 21.985 9.891 19.313 9.891 16.017 C9.891 12.72 12.563 10.048 15.86 10.048 C19.156 10.048 21.828 12.72 21.828 16.017 C21.828 19.313 19.156 21.985 15.86 21.985" id="Fill-54" fill="#A3D1A1"></path>
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </svg>`
    }
  }

  buildViewLocationLink(resultNameLinkString) {
    const parser = new DOMParser();
    const linkElement = parser.parseFromString(resultNameLinkString, 'text/html');
    const locationLink = linkElement.body.firstElementChild.href;
    return `<a href="${locationLink}"><svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="#000000"><path d="M0 0h24v24H0V0z" fill="none"/><path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/></svg>${this.viewDetailsLabel}</a>`;
  }

  getTodaysHours(daysString, hoursString) {
    const daysArray = daysString.split(",");
    const hoursArray = hoursString.split(",");
    const todayDayOfWeekString = this.daysOfWeekArray[dayjs().day()];
    const matchingDayOfWeekIndex = daysArray.indexOf(todayDayOfWeekString);

    if (matchingDayOfWeekIndex >= 0) {
      return hoursArray[matchingDayOfWeekIndex];
    }
    return null;
  }

  getTodaysHoursString(daysString, hoursString) {
    const todaysHours = this.getTodaysHours(daysString, hoursString);

    if (todaysHours) {
      return `<p class="hours-text">${this.todaysHoursLabel}</p><p class="hours">${todaysHours}</p>`;
    }
  }

  setPageInfo() {
    this.itemsPerPage = this.results.pager[SearchResultsConstants.ITEMS_PER_PAGE];
    this.totalPages = this.results.pager[SearchResultsConstants.TOTAL_PAGES];
  }

  updatePagination() {
    ReactDOM.render(
      <BrowserRouter>
        <Pagination
          pageLabel={this.pageLabel}
          ofLabel={this.ofLabel}
          totalPages={this.totalPages}
          paginationHandler={this.getResults}
        />
      </BrowserRouter>,
      this.paginationContainer
    );
  }

  buildMilesAwayText(kilometers) {
    if (kilometers > 0) {
      return `<p class="miles-away-text">${this.geolocationService.convertKilometersToMiles(kilometers).toFixed(2)} ${this.milesLabel} ${this.awayLabel}</p>`
    } else {
      return '';
    }
  }

  updateResultsCards() {
    this.showLoadedState();

    ReactDOM.render(
      <>
        {this.results.rows.map(result => {
          return (
            <LocationResultCard
              key={result.title}
              locationName={result['field_location_name']}
              locationAddress={result['field_location_address']}
              locationImageElement={this.getLocationImage(result['hero_url'], result['hero_alt'])}
              locationHours={this.getTodaysHoursString(result['field_day'], result['field_hours'])}
              locationPhone={result['field_phone_1']}
              milesAwayText={this.buildMilesAwayText(result['field_geolocation_distance'])}
              directionsLabel={this.directionsLabel}
              openDirections={this.openDirections}
              viewLocationLink={this.buildViewLocationLink(result['field_location_name'])}
            />
          );
        })}
      </>,
      this.locationsCardsContainer
    );
  }

  updateContainer() {
    ReactDOM.render(
      <>
        <LocationResults
          key={`location-results`}
          resultsCountText={this.buildLocationCountText()}
        />
      </>,
      this.locationResultsContainer
    );
    this.locationsCardsContainer = this.locationResultsContainer.querySelector('.location-results-card--container');
    this.updateResultsCards();
  }
}
